import { useMutation, useQueryClient } from '@tanstack/react-query';
import UserService from '../services/user.service';
import { SetDemoAccountPasswordRequest } from '../common/model/dto/set-demo-account-password-request.ts';
import { UserInfo } from '../common/model/dto/user-info';
import { AdminClinicLocationProviderService } from '../services/admin/admin-clinic-location-provider.service';
import { CancelActiveScanningPlanRequest } from '../common/model/dto/scan-sequences/cancel-active-scanning-plan-request';

export function useAdminUserMutations(userId: string) {
  const qc = useQueryClient();

  const invalidateUser = () =>
    qc.invalidateQueries({ queryKey: ['admin-user', userId] });
  const invalidateUsers = () =>
    qc.invalidateQueries({ queryKey: ['admin-users'] });

  const deactivateUser = useMutation({
    mutationFn: () => UserService.deactivateUserById(userId),
    onSuccess: () => {
      invalidateUser();
      invalidateUsers();
    },
  });

  const deleteUser = useMutation({
    mutationFn: () => UserService.deleteUserById(userId),
    onSuccess: invalidateUsers,
  });

  const deleteDemoAccountPassword = useMutation({
    mutationFn: () => UserService.deleteDemoAccountPassword(userId),
    onSuccess: invalidateUser,
  });

  const setDemoAccountPassword = useMutation({
    mutationFn: (payload: SetDemoAccountPasswordRequest) =>
      UserService.setPasswordForDemoAccount(userId, payload),
    onSuccess: invalidateUser,
  });

  const updateUserInfo = useMutation({
    mutationFn: (updates: Partial<UserInfo>) =>
      UserService.updateUserInfo(userId, updates),
    onSuccess: invalidateUser,
  });

  const providerUserInfoUpdate = useMutation({
    mutationFn: ({
      clinicLocationProviderId,
      updatedProperties,
    }: {
      clinicLocationProviderId: string;
      updatedProperties: any;
    }) =>
      AdminClinicLocationProviderService.updateClinicLocationProviderRelation(
        clinicLocationProviderId,
        updatedProperties
      ),
    onSuccess: invalidateUser,
  });

  const cancelActiveScanningPlanForUser = useMutation({
    mutationFn: (
      cancelActiveScanningPlanRequest: CancelActiveScanningPlanRequest
    ) =>
      UserService.cancelActiveScanningPlanForUser(
        userId,
        cancelActiveScanningPlanRequest
      ),
    onSuccess: invalidateUser,
  });

  return {
    deactivateUser,
    deleteUser,
    deleteDemoAccountPassword,
    setDemoAccountPassword,
    updateUserInfo,
    providerUserInfoUpdate,
    cancelActiveScanningPlanForUser,
  };
}
